import { useRef } from 'react';
import styled from 'styled-components';
import { Emoji, EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import { ICommentReactionFieldsFragment } from '../../../../../../utils/apollo/graphqlSchema';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../../../../ActionsVisibilityContext';
import { theme } from '../../../../../../styles/theme';
import useUser from '../../../../../../utils/hooks/useUser';
import AddCommentReaction from '../AddCommentReaction';

interface IProps {
  reactions: ICommentReactionFieldsFragment[];
  onAdd: (unifiedEmoji: string) => void;
  onRemove: (unifiedEmoji: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
  .epr-emoji-native {
    line-height: 1;
    font-family: inherit !important;
  }
`;
const EmojiContainer = styled.button<{ $isUserReacted: boolean }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 4px;
  padding: 4px 8px;
  border: 1px solid
    ${({ $isUserReacted }) =>
      $isUserReacted ? 'transparent' : theme.colors.blue500};
  border-radius: 4px;
  background-color: ${({ $isUserReacted }) =>
    $isUserReacted ? theme.colors.blue500 : 'transparent'};
  &:hover {
    border-color: ${theme.colors.mainBlue};
    cursor: pointer;
  }
`;
const EmojiCounter = styled.div`
  font-size: 10px;
  color: ${theme.colors.secondary900};
`;

const CommentReactions = ({ reactions, onAdd, onRemove }: IProps) => {
  const pickerOpened = useRef(false);
  const currentUser = useUser();

  const { actionsVisible, showActions, hideActions } =
    useActionsVisibilityContext();

  const handleMouseEnter = () => {
    if (!pickerOpened.current) {
      showActions();
    }
  };
  const handleMouseLeave = () => {
    if (!pickerOpened.current) {
      hideActions();
    }
  };
  const handleOpenEmojis = () => {
    pickerOpened.current = true;
  };
  const handleCloseEmojis = () => {
    pickerOpened.current = false;
    if (actionsVisible) {
      hideActions();
    }
  };

  const handleClick = (emojiKey: string, isUserReacted: boolean) => {
    if (isUserReacted) {
      onRemove(emojiKey);
    } else {
      onAdd(emojiKey);
    }
  };

  const handleSelect = (emojiData: EmojiClickData) => {
    // When we already have a list of reactions, and we select an emoji from
    // the picker, we have to:
    // 1. Check if it's a new reaction
    // 2. Check if user has already reacted with this emoji:
    // 2.a If yes - remove reaction
    // 2.b If no - add reaction
    const emojiKey = emojiData.unified;
    const reaction = reactions.find(
      (reaction) => reaction.emojiKey === emojiKey,
    );

    // If nothing found - add a new reaction
    if (!reaction) {
      onAdd(emojiKey);
      return;
    }

    // Check if user has already reacted with this emoji
    const isUserReacted = reaction.users.some(
      (user) => user.id === currentUser.id,
    );
    handleClick(emojiKey, isUserReacted);
  };

  return (
    <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {reactions.map(({ emojiKey, users }) => {
        const isUserReacted = users.some((user) => user.id === currentUser.id);

        return (
          <EmojiContainer
            key={emojiKey}
            type="button"
            onClick={() => handleClick(emojiKey, isUserReacted)}
            $isUserReacted={isUserReacted}
          >
            <Emoji
              size={14}
              unified={emojiKey}
              emojiStyle={EmojiStyle.NATIVE}
            />
            <EmojiCounter>{users.length}</EmojiCounter>
          </EmojiContainer>
        );
      })}
      {actionsVisible && (
        <AddCommentReaction
          onSelect={handleSelect}
          onOpen={handleOpenEmojis}
          onClose={handleCloseEmojis}
        />
      )}
    </Container>
  );
};

export default withActionsVisibilityContext(CommentReactions);
